import { Button } from "./ui/button";
import { Check, Star, ArrowRight, ShoppingCart } from "lucide-react";

// Digistore24-Checkout-Platzhalter. Sobald die Produkte in Digistore24 angelegt sind,
// hier die echten Produkt-IDs eintragen (z. B. https://www.digistore24.com/product/<PRODUCT_ID>).
const DIGISTORE_PRO_URL = "https://www.digistore24.com/product/PRO_PROMPTKISTE_PLACEHOLDER";
const DIGISTORE_BUSINESS_URL = "https://www.digistore24.com/product/BUSINESS_PROMPTKISTE_PLACEHOLDER";

const TIERS = [
  {
    name: "Free",
    price: "0€",
    period: "/ für immer",
    description: "Perfekt zum Ausprobieren.",
    features: [
      "3 Prompt-Generierungen pro Tag",
      "Standardqualität",
      "Copy- & Download-Funktion",
      "Alle Genres & Stimmungen",
    ],
    cta: "Kostenlos starten",
    ctaType: "scroll",
    target: "generator",
    highlight: false,
    testId: "tier-free",
  },
  {
    name: "Pro",
    price: "9,99€",
    period: "/ Monat",
    description: "Für ernsthafte Creator.",
    features: [
      "Unbegrenzte Generierungen",
      "Detailliertere, bessere Prompts",
      "Genre-Mix Funktion",
      "TikTok Hook Ideen",
      "Priorisierte Generierung",
    ],
    cta: "Jetzt kaufen",
    ctaType: "checkout",
    target: DIGISTORE_PRO_URL,
    highlight: true,
    badge: "Beliebtester Plan",
    testId: "tier-pro",
  },
  {
    name: "Business",
    price: "29€",
    period: "/ Monat",
    description: "Für Teams & Agenturen.",
    features: [
      "Alles aus Pro",
      "Team-Nutzung (5 Sitze)",
      "Prompt-Historie",
      "Export-Funktionen (CSV, JSON)",
      "Zukünftige API-Features",
    ],
    cta: "Jetzt kaufen",
    ctaType: "checkout",
    target: DIGISTORE_BUSINESS_URL,
    highlight: false,
    testId: "tier-business",
  },
];

const handleCta = (tier) => {
  if (tier.ctaType === "scroll") {
    const el = document.getElementById(tier.target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else if (tier.ctaType === "checkout") {
    window.open(tier.target, "_blank", "noopener,noreferrer");
  }
};

export const Pricing = ({ premium = false }) => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 px-6 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className="text-xs uppercase tracking-[0.2em] text-[#CCFF00] font-semibold">
            Preise
          </span>
          <h2 className="font-display mt-3 text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white">
            Fair. Transparent. Skalierbar.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
            Starte kostenlos. Upgrade, wenn du mehr willst. Jederzeit kündbar.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              data-testid={tier.testId}
              className={`relative rounded-3xl p-8 flex flex-col transition-transform hover:-translate-y-1 ${
                tier.highlight
                  ? "bg-gradient-to-b from-[#1a1f0a] to-[#0F1014] border border-[#CCFF00]/40 md:scale-[1.04] shadow-[0_0_48px_-12px_rgba(204,255,0,0.35)]"
                  : "bg-[#0F1014] border border-white/5"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-[#CCFF00] text-black text-xs font-bold px-3 py-1 rounded-full">
                  <Star className="w-3 h-3" fill="currentColor" />
                  {tier.badge}
                </div>
              )}

              <div className="mb-6">
                <h3 className="font-display text-2xl font-bold text-white">
                  {tier.name}
                </h3>
                <p className="text-sm text-zinc-400 mt-1">{tier.description}</p>
              </div>

              <div className="mb-8 flex items-baseline gap-1">
                <span
                  className={`font-display text-5xl font-black tracking-tight ${
                    tier.highlight ? "text-[#CCFF00]" : "text-white"
                  }`}
                >
                  {tier.price}
                </span>
                <span className="text-sm text-zinc-500">{tier.period}</span>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {tier.features.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-sm text-zinc-300"
                  >
                    <span
                      className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                        tier.highlight
                          ? "bg-[#CCFF00]/15"
                          : "bg-white/5"
                      }`}
                    >
                      <Check
                        className={`w-3 h-3 ${
                          tier.highlight ? "text-[#CCFF00]" : "text-zinc-300"
                        }`}
                        strokeWidth={3}
                      />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`${tier.testId}-cta`}
                onClick={() => handleCta(tier)}
                disabled={premium && tier.name === "Pro"}
                className={
                  tier.highlight
                    ? "bg-[#CCFF00] hover:bg-[#B3E600] disabled:bg-white/10 disabled:text-zinc-400 disabled:cursor-not-allowed text-black font-bold rounded-xl h-12 transition-all hover:shadow-[0_0_28px_-6px_rgba(204,255,0,0.6)] group"
                    : "bg-white/[0.07] hover:bg-white/[0.12] text-white border border-white/10 rounded-xl h-12 group"
                }
              >
                {premium && tier.name === "Pro" ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="w-4 h-4" strokeWidth={3} />
                    Aktiver Plan
                  </span>
                ) : (
                  <>
                    {tier.ctaType === "checkout" ? (
                      <ShoppingCart className="w-4 h-4 mr-2" strokeWidth={2.2} />
                    ) : null}
                    {tier.cta}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                  </>
                )}
              </Button>
              {tier.ctaType === "checkout" && (
                <p
                  data-testid={`${tier.testId}-checkout-note`}
                  className="mt-3 text-[11px] text-zinc-500 text-center leading-relaxed"
                >
                  Die Abbuchung erfolgt durch{" "}
                  <a
                    href="https://www.digistore24.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-zinc-400 hover:text-[#CCFF00] underline underline-offset-2"
                  >
                    Digistore24.com
                  </a>
                  .
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
